import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getPreviewProfiles } from '../../services/profile';
import AuthContext from '../Context/AuthProvider';
import PaymentModal from '../common/PaymentModal';
import { toast } from 'react-toastify';
import { getAge } from '../common/utilities/AgeCalculator';
import { BACKEND_IMAGE_URL } from '../../services/Constants';
import PreviewModal from '../acknowledgements/PreviewModal';

const ALLOWED_PROFILE_VIEWS = 2;

const PreviewedProfiles = () => {
  const { getToken } = React.useContext(AuthContext);
  const [profiles, setProfiles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const [previewModalOpen, setPreviewModalOpen] = React.useState(false);
  const [fetchError, setFetchError] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetchProfiles = async () => {
      const auth = getToken();
      if (auth) {
        try {
          const response = await getPreviewProfiles(auth.token);
          if (response.data.errors) {
            throw new Error(response.data.errors);
          } else {
            setProfiles(response.data.data.getPreviewProfiles);
            setFetchError(false);
          }
        } catch (error) {
          console.error('Error fetching previewed profiles:', error);
          toast.error('There was an issue in fetching previewed profiles. Please try again later.', { timeOut: 4000 });
          setFetchError(true);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProfiles();
  }, []);

  const handleProfileClick = (profileId) => {
    navigate(`/profile/${profileId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <span className="loading loading-dots loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Previewed Profiles</h1>
        {fetchError ? (
          <p className="text-red-600">We couldn&apos;t load your previewed profiles. Please try again later.</p>
        ) : profiles.length === 0 ? (
          <p className="text-gray-600">
            You haven't previewed any profiles yet.{' '}
            <a href="/" className="text-primary font-semibold" style={{textDecoration: 'none'}}>Go to Homepage</a>.
          </p>
        ) : (
          <p className="text-gray-600">
            You have used {profiles.length}/{ALLOWED_PROFILE_VIEWS} profile previews
          </p>
        )}
      </div>
      {/* Upgrade Section - Show always */}
      <div className="text-center bg-gray-50 rounded-lg p-6 mb-8">
        <h2 className="text-2xl font-bold mb-4">Want to See More Profiles?</h2>
        <p className="text-gray-600 mb-6">
          Upgrade to our annual subscription to access all profiles and connect with potential matches.
        </p>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => setShowPaymentModal(true)}
            className="bg-white text-primary border border-primary px-6 py-3 rounded-lg font-semibold transition-transform duration-200 hover:scale-105"
          >
            Upgrade Now
          </button>
          <button
            onClick={() => setPreviewModalOpen(true)}
            className="bg-[#0841f0] text-white border border-primary px-6 py-3 rounded-lg font-semibold transition-transform duration-200 hover:scale-105"
          >
            How Preview Works
          </button>
        </div>
      </div>

      {/* Profile Cards Grid - Centered with max-width */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {profiles.map((profile) => (
            <div
              key={profile.id}
              className="relative rounded-lg overflow-hidden shadow-lg cursor-pointer transform transition-transform hover:scale-105 w-full sm:max-w-xs mx-auto"
              onClick={() => handleProfileClick(profile.id)}
            >
              {/* Profile Image - Consistent aspect ratio and centering */}
              <div className="aspect-w-3 aspect-h-4 w-full bg-gray-100 flex items-center justify-center">
                <img
                  src={`${BACKEND_IMAGE_URL}/images/profile?userId=${profile.id}`}
                  alt={`${profile.firstName} ${profile.lastName}`}
                  className="object-cover object-center w-full h-full rounded"
                />
              </div>
              {/* Overlay with Name, Age, and Work Info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <h3 className="text-white text-xl font-semibold">
                  {profile.firstName} {profile.lastName}
                </h3>
                <p className="text-white text-sm mb-1">
                  {getAge(profile.dob)}
                </p>
                <p className="text-white text-sm">
                  {profile.occupation || 'Occupation not specified'}
                  {profile.employedIn ? ` at ${profile.employedIn}` : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <PaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
      />

      <PreviewModal
        open={previewModalOpen}
        handleClose={() => setPreviewModalOpen(false)}
        onUpgrade={() => {
          setPreviewModalOpen(false);
          setShowPaymentModal(true);
        }}
      />
    </div>
  );
};

export default PreviewedProfiles; 